import React, { useMemo, useState, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import { Loading, Button, Icon, Tooltip } from '../../atoms'
import { SearchableList } from '../SearchableList'
import {
  FileCategorySection,
  FileUploader,
  FilesEmptyState,
  isPreviewableFile,
} from '../Files'
import { FilePreviewModal } from '../FilePreviewModal'
import { HeaderActionButton } from './EntityFilesContent.styles'
import type {
  EntityFile,
  EntityFilesContentProps,
  FileCategory,
} from './EntityFilesContent.types'

export const EntityFilesContent: React.FC<EntityFilesContentProps> = ({
  entityId,
  files,
  loading,
  error,
  activeCategory,
  fileService,
  uploadFile,
  deleteFile,
  refreshFiles,
  onClose,
  readOnly = false,
  emptyTitle,
  emptyDescription,
}) => {
  const { t } = useTranslation('ui')

  // State for file preview modal
  const [previewState, setPreviewState] = useState<{
    isOpen: boolean
    url: string
    name: string
    type: 'pdf' | 'image'
  } | null>(null)

  // State for delete confirmation
  const [deletingFileId, setDeletingFileId] = useState<string | null>(null)

  // State for search
  const [searchQuery, setSearchQuery] = useState('')

  // Ref for hidden file input
  const fileInputRef = useRef<HTMLInputElement>(null)

  // Filter files based on search query
  const filteredFiles = useMemo(() => {
    if (!files) return []
    if (!searchQuery.trim()) return files

    const query = searchQuery.toLowerCase()
    return files.filter((file) => {
      const searchText =
        `${file.name || ''} ${file.original_name || ''} ${file.description || ''} ${file.uploaded_by_name || ''} ${file.category || ''}`.toLowerCase()
      return searchText.includes(query)
    })
  }, [files, searchQuery])

  // Handle file input change
  const handleFileInputChange = async (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const files = event.target.files
    if (!files) return

    for (let i = 0; i < files.length; i++) {
      const file = files[i]
      try {
        if (uploadFile) await uploadFile(entityId, file)
      } catch (error) {
        console.error('Upload failed:', error)
      }
    }

    // Reset the input
    if (fileInputRef.current) {
      fileInputRef.current.value = ''
    }
  }

  // Trigger file input click
  const handleUploadClick = () => {
    fileInputRef.current?.click()
  }

  // Group filtered files by category
  const groupedFiles = useMemo(() => {
    const groups = new Map<FileCategory, EntityFile[]>()

    // Initialize empty arrays for each category
    const categories: FileCategory[] = [
      'PDF Documents',
      'Spreadsheets',
      'CAD Files',
      'Images',
      'Documents',
      'Other',
    ]
    categories.forEach((cat) => groups.set(cat, []))

    // Group filtered files by category
    filteredFiles.forEach((file) => {
      // Only include files that match the category filter
      if (activeCategory === 'all' || file.category === activeCategory) {
        const category = file.category || 'Other'
        const categoryFiles = groups.get(category) || groups.get('Other')!
        categoryFiles.push(file)
      }
    })

    // Convert to array format and filter out empty categories
    return Array.from(groups.entries()).filter(([, files]) => files.length > 0)
  }, [filteredFiles, activeCategory])

  const handleFileUpload = async (file: File) => {
    if (readOnly || !uploadFile) return
    try {
      await uploadFile(entityId, file)
    } catch (error) {
      console.error('Upload failed:', error)
    }
  }

  // Card click - preview if possible, download otherwise
  const handleCardClick = async (file: EntityFile) => {
    try {
      if (isPreviewableFile(file.name) && fileService?.previewFile) {
        const url = await fileService.previewFile(file.id)
        const isPdf = file.name.toLowerCase().endsWith('.pdf')
        setPreviewState({
          isOpen: true,
          url,
          name: file.name,
          type: isPdf ? 'pdf' : 'image',
        })
      } else if (fileService?.downloadFile) {
        await fileService.downloadFile(file.id)
      } else {
        console.warn('No file service provided for download functionality')
      }
    } catch (error) {
      console.error('Preview failed:', error)
    }
  }

  // Download button - always download
  const handleDownload = async (file: EntityFile) => {
    try {
      if (fileService?.downloadFile) {
        await fileService.downloadFile(file.id)
      }
    } catch (error) {
      console.error('Download failed:', error)
    }
  }

  const handleClosePreview = () => {
    setPreviewState(null)
  }

  // Show delete confirmation for a file
  const handleDelete = (file: EntityFile) => {
    setDeletingFileId(file.id)
  }

  // Cancel delete confirmation
  const handleCancelDelete = () => {
    setDeletingFileId(null)
  }

  // Confirm and execute delete
  const handleConfirmDelete = async (file: EntityFile) => {
    if (readOnly || !deleteFile) return
    try {
      await deleteFile(file.id)
      setDeletingFileId(null)
    } catch (error) {
      console.error('Delete failed:', error)
      setDeletingFileId(null)
    }
  }

  if (loading) {
    return (
      <div
        style={{
          height: '100%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <Loading />
      </div>
    )
  }

  if (error) {
    return (
      <div
        style={{
          height: '100%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: '24px',
        }}
      >
        <div style={{ textAlign: 'center' }}>
          <p>Error: {error}</p>
          {refreshFiles && (
            <Button onClick={refreshFiles}>{t('files.retry')}</Button>
          )}
        </div>
      </div>
    )
  }

  return (
    <div style={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      {!readOnly && (
        <input
          ref={fileInputRef}
          type="file"
          style={{ display: 'none' }}
          onChange={handleFileInputChange}
          multiple
          accept=".pdf,.doc,.docx,.xls,.xlsx,.png,.jpg,.jpeg,.dwg,.dxf,.webp"
        />
      )}

      <SearchableList
        icon="document"
        title={t('files.title')}
        count={filteredFiles.length}
        searchPlaceholder={t('files.searchPlaceholder')}
        onSearch={setSearchQuery}
        initialQuery={searchQuery}
        onClose={onClose}
        searchable
        closable={Boolean(onClose)}
        searchThreshold={0}
        emptyStateComponent={
          <FilesEmptyState title={emptyTitle} description={emptyDescription} />
        }
        style={{ flex: 1 }}
        searchAction={
          readOnly ? undefined : (
            <Tooltip content={t('files.uploadFile')}>
              <HeaderActionButton
                onClick={handleUploadClick}
                aria-label={t('files.uploadFile')}
              >
                <Icon name="plus" size="small" />
              </HeaderActionButton>
            </Tooltip>
          )
        }
        noResultsComponent={
          filteredFiles.length === 0 && searchQuery.trim() ? undefined : (
            <>
              {groupedFiles.map(([category, categoryFiles]) => (
                <FileCategorySection
                  key={category}
                  category={category}
                  files={categoryFiles}
                  onClick={handleCardClick}
                  onDownload={handleDownload}
                  onDelete={readOnly ? undefined : handleDelete}
                  deletingFileId={deletingFileId}
                  onCancelDelete={handleCancelDelete}
                  onConfirmDelete={handleConfirmDelete}
                />
              ))}
            </>
          )
        }
        searchResultsRenderer={(_, __) =>
          searchQuery.trim() && files
            ? filteredFiles.length === 1
              ? t('files.searchResults', {
                  count: filteredFiles.length,
                  total: files.length,
                })
              : t('files.searchResultsPlural', {
                  count: filteredFiles.length,
                  total: files.length,
                })
            : ''
        }
      >
        {groupedFiles.map(([category, categoryFiles]) => (
          <FileCategorySection
            key={category}
            category={category}
            files={categoryFiles}
            onClick={handleCardClick}
            onDownload={handleDownload}
            onDelete={readOnly ? undefined : handleDelete}
            deletingFileId={deletingFileId}
            onCancelDelete={handleCancelDelete}
            onConfirmDelete={handleConfirmDelete}
          />
        ))}
      </SearchableList>

      {!readOnly && uploadFile && (
        <FileUploader onUpload={handleFileUpload} multiple />
      )}

      {previewState && (
        <FilePreviewModal
          isOpen={previewState.isOpen}
          onClose={handleClosePreview}
          fileUrl={previewState.url}
          fileName={previewState.name}
          fileType={previewState.type}
        />
      )}
    </div>
  )
}
